import React from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../components/Icon";

const SKELETON_GROUPS = [
    { label: "A", count: 3 },
    { label: "B", count: 2 },
    { label: "C", count: 2 },
];

const METHOD_WIDTHS = ["w-10", "w-12", "w-8", "w-10", "w-14"];
const PATH_WIDTHS = ["w-40", "w-52", "w-36", "w-48", "w-32", "w-44", "w-56"];

export default function DiscoverySkeleton({ phase }) {
    const { t } = useTranslation();
    const isCrawling = phase === "crawling";
    let idx = 0;

    return (
        <div className="max-w-2xl mx-auto space-y-6">
            {/* Status indicator */}
            <div className="flex flex-col items-center gap-4 pt-6 pb-2">
                <div className="relative">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-200">
                        <Icon name={isCrawling ? "Globe" : "Search"} className="w-6 h-6 text-white animate-pulse" />
                    </div>
                    <span className="absolute -bottom-1 -right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center shadow">
                        <Icon name="Loader" className="w-3 h-3 text-blue-600 animate-spin" />
                    </span>
                </div>
                <div className="text-center space-y-1">
                    <p className="text-sm font-semibold text-gray-800">
                        {t(isCrawling ? "builder.skeleton.crawling" : "builder.skeleton.discovering")}
                    </p>
                    <p className="text-xs text-gray-400">
                        {t(isCrawling ? "builder.skeleton.crawlingSub" : "builder.skeleton.discoveringSub")}
                    </p>
                </div>
            </div>

            {/* Shimmer endpoint skeletons */}
            <div className="space-y-3 opacity-60">
                {/* Summary bar skeleton */}
                <div className="flex items-center justify-between">
                    <div className="h-4 w-36 bg-gray-200 rounded animate-pulse" />
                    <div className="flex gap-1.5">
                        <div className="h-4 w-12 bg-gray-200 rounded animate-pulse" />
                        <div className="h-4 w-14 bg-gray-200 rounded animate-pulse" />
                        <div className="h-4 w-10 bg-gray-200 rounded animate-pulse" />
                    </div>
                </div>

                {/* Groups */}
                {SKELETON_GROUPS.map((group, gi) => (
                    <div key={gi} className="space-y-1.5" style={{ animationDelay: `${gi * 200}ms` }}>
                        {/* Resource label */}
                        <div className="flex items-center gap-1.5">
                            <div className="w-3 h-3 bg-gray-200 rounded animate-pulse" />
                            <div className="h-3 w-20 bg-gray-200 rounded animate-pulse" />
                            <div className="h-3 w-6 bg-gray-200 rounded animate-pulse" />
                        </div>
                        {/* Endpoint rows */}
                        <div className="space-y-1 ml-4">
                            {Array.from({ length: group.count }, (_, ei) => {
                                const row = idx++;
                                return (
                                    <div
                                        key={ei}
                                        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-100 bg-white"
                                        style={{ animationDelay: `${row * 120}ms` }}
                                    >
                                        <div className={`h-4 ${METHOD_WIDTHS[row % METHOD_WIDTHS.length]} bg-gray-200 rounded animate-pulse`} />
                                        <div className={`h-3.5 ${PATH_WIDTHS[row % PATH_WIDTHS.length]} bg-gray-100 rounded animate-pulse`} />
                                        <div className="flex-1" />
                                        <div className="h-3 w-24 bg-gray-100 rounded animate-pulse hidden sm:block" />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            {/* Scanning progress dots */}
            <div className="flex items-center justify-center gap-1.5 pt-2">
                {[0, 1, 2].map((i) => (
                    <div
                        key={i}
                        className="w-1.5 h-1.5 rounded-full bg-blue-400"
                        style={{
                            animation: "pulse 1.4s ease-in-out infinite",
                            animationDelay: `${i * 200}ms`,
                        }}
                    />
                ))}
            </div>
        </div>
    );
}
